import { RichText } from '@brunommsilva/react-foundations';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useLayout from '../../../hooks/layout/use-layout';
import AssistantAvatar from './assistant-avatar';

interface AssistantHeaderProps {
  title: string | null;
  canClear: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
  onClear: () => void;
  onClose: () => void;
}

export default function AssistantHeader({
  title,
  canClear,
  isPinned,
  onTogglePin,
  onClear,
  onClose,
}: AssistantHeaderProps) {
  const { t } = useTranslation();
  const { topBarHeight } = useLayout();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        minHeight: isPinned ? topBarHeight : undefined,
        // As the top bar of the app: the colour is left to the avatar.
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        color: 'text.secondary',
      }}
    >
      <AssistantAvatar size={34} iconSize={20} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}
        >
          {t('ASSISTANT_TITLE')}
        </Typography>
        {title && (
          <Typography variant="caption" noWrap sx={{ display: 'block' }}>
            <RichText text={title} emoji />
          </Typography>
        )}
      </Box>
      <Tooltip title={t(isPinned ? 'ASSISTANT_UNPIN' : 'ASSISTANT_PIN')}>
        <IconButton
          size="small"
          color={isPinned ? 'primary' : 'inherit'}
          aria-label={t(isPinned ? 'ASSISTANT_UNPIN' : 'ASSISTANT_PIN')}
          aria-pressed={isPinned}
          onClick={onTogglePin}
        >
          {isPinned ? (
            <PushPinIcon fontSize="small" />
          ) : (
            <PushPinOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      {canClear && (
        <Tooltip title={t('ASSISTANT_CLEAR')}>
          <IconButton
            size="small"
            color="inherit"
            aria-label={t('ASSISTANT_CLEAR')}
            onClick={onClear}
          >
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        size="small"
        color="inherit"
        aria-label={t('ASSISTANT_CLOSE')}
        onClick={onClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
